
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler, LOCALE_ID, inject, provideAppInitializer, Injector } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppBaseConfiguration, FactorConfigurationService, AppConstants, NotificationMessageComponent, VersionInterceptor, ErrorInterceptor, getDatepickerConfig, getDaterangepickerConfig, AppMissingTranslationHandler, TelemetryInitializer, LoaderSpinnerComponent, ValidationService } from '@aston/foundation';
import { ThemesModule, CSSThemeService, BusinessThemeService, DefaultBusinessThemeService } from '@aston/themes';
import { filter } from 'rxjs/operators';
import { TranslateModule, MissingTranslationHandler, TranslateLoader } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { provideEnvironmentNgxCurrency } from 'ngx-currency';
import { BsDatepickerConfig, BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';

import themes from '../assets/sass/themes';

import { AppStoreActions, AppStoreSelectors } from './root-store/app-store';
import { FederationsModule } from './federations-module/federations.module';
import { AccountModule } from './account-module/account.module';
import { AuthenticationModule } from './authentication-module/authentication.module';
import { AppConstants as ConsoleAppConstants } from './app.constants';
import { ConsoleValidationService, ConsoleFactorConfigurationService, provideConsoleApiRoot } from './shared-module/services';
import { AppErrorHandler } from './shared-module/handlers';
import { RootStoreModule } from './root-store';
import { AppConfiguration } from './app.configuration';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { translateLoader } from './app.translation';
import { provideAIChat, provideDebugBar } from './app.connectors';
import { SubsidiariesPageComponent } from './subsidiaries-module/pages/subsidiaries-page/subsidiaries-page.component';
import { SubsidiaryDetailCompanyPageComponent } from './subsidiaries-module/pages/subsidiary-detail-company-page/subsidiary-detail-company-page.component';
import { SubsidiaryDetailUsersPageComponent } from './subsidiaries-module/pages/subsidiary-detail-users-page/subsidiary-detail-users-page.component';

// override constants with our own
AppConstants.overrideWith(ConsoleAppConstants);

// import the locale, used in currencyPipe for format
registerLocaleData(localeFr);


@NgModule({
	declarations: [
		AppComponent
	],
	bootstrap: [AppComponent],
	imports: [
		AccountModule,
		AppRoutingModule,
		AuthenticationModule,
		BrowserAnimationsModule,
		BrowserModule,
		TranslateModule.forRoot({
			missingTranslationHandler: {
				provide: MissingTranslationHandler,
				useClass: AppMissingTranslationHandler,
			},
			loader: {
				provide: TranslateLoader,
				useFactory: (translateLoader),
				deps: [HttpClient],
			}
		}),
		ThemesModule.forRoot({
			themes,
			cssThemeService: {
				provide: CSSThemeService,
				useClass: CSSThemeService
			},
			businessThemeService: {
				provide: BusinessThemeService,
				useClass: DefaultBusinessThemeService
			},
		}),
		CommonModule,
		FederationsModule,
		LoaderSpinnerComponent,
		NotificationMessageComponent,
		RootStoreModule,
		SubsidiariesPageComponent,
		SubsidiaryDetailCompanyPageComponent,
		SubsidiaryDetailUsersPageComponent
	],
	providers: [
		{
			provide: LOCALE_ID,
			useValue: 'fr-FR'
		},
		provideAppInitializer(() => {
			const initializerFn = (AppConfigurationFactory)(inject(Store));
			return initializerFn();
		}),
		AppConfiguration,
		{
			provide: AppBaseConfiguration,
			useExisting: AppConfiguration
		},
		{
			provide: ValidationService,
			useClass: ConsoleValidationService,
		},
		{
			provide: FactorConfigurationService,
			useClass: ConsoleFactorConfigurationService,
		},
		provideEnvironmentNgxCurrency(ConsoleAppConstants.CURRENCY_MASK_OPTIONS),
		{
			provide: ErrorHandler,
			useClass: AppErrorHandler
		},
		{
			provide: TelemetryInitializer,
			useValue: null,
		},
		{ provide: HTTP_INTERCEPTORS, useClass: VersionInterceptor, multi: true, deps: [Injector] },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true, deps: [Injector] },
		{
			provide: BsDatepickerConfig,
			useFactory: getDatepickerConfig
		},
		{
			provide: BsDaterangepickerConfig,
			useFactory: getDaterangepickerConfig
		},
		provideAIChat(),
		provideDebugBar(),
		provideConsoleApiRoot(),
		provideHttpClient(withInterceptorsFromDi())
	]
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AppModule {
}


export function AppConfigurationFactory(store: Store) {
	return () => new Promise(resolve => {
		store.dispatch(AppStoreActions.StartInitializer());
		store.select(AppStoreSelectors.selectIsInitFinished).pipe(filter(ready => ready)).subscribe(_ => {
			resolve(true);
		});
	});
}
